/**
 * /2fa-setup — TOTP enrolment.
 *
 * Reached from two distinct entry points (both wired through this one
 * route, distinguished by `?required=`):
 *
 *  1. Optional — a user clicks "Set up two-factor authentication" from the
 *     account menu while already signed-in. Skippable; the post-setup
 *     redirect is ?next= (defaults to /dashboard).
 *  2. M-1 mandatory gate — a super_admin/hr_manager identity tried to
 *     log in / activate / register and the backend 403'd
 *     `totp_enrollment_required`, leaving an enrolment-scoped
 *     identity-only session (no tenant, no refresh, no CSRF row). The
 *     login/activate caller bounced us here with `?required=1`. UI has
 *     no skip affordance in this mode.
 *
 * Passkeys (spec §6.1): the page first offers two choices — "Add a
 * passkey (recommended)" and "Use an authenticator app" (straight to the
 * authenticator when the browser cannot use passkeys). The authenticator
 * secret is only staged once that option is chosen.
 *
 * After the first method is confirmed (a passkey, or the authenticator
 * code) we re-hydrate /me. If user_id is still 0 (the M-1 enrolment-only
 * session) we open the only workspace directly, or route to
 * /select-workspace when there are several; otherwise we honour ?next=.
 * A session still on a temporary password is sent to /change-password
 * before anything is set up (round 2 T-H1). Adding a passkey when the person
 * already has a method needs a fresh step-up: the in-place StepUpPrompt
 * (mounted here — the workspace layout is not) confirms it, then the
 * registration is retried once.
 *
 * Migration note: 1:1 port of
 * `frontend/src/app/(auth)/2fa-setup/page.tsx`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Copy, KeyRound, Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi, toApiError } from "@/lib/api";
import { useAuth, safeNext } from "@/lib/auth";
import { totpCodeSchema, type TotpCodeValues } from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";
import { twoFactorSetupDescription, twoFactorSetupReason } from "@/lib/auth/auth-copy";
import {
  defaultPasskeyName,
  isAlreadyRegisteredError,
  isPasskeyCancelled,
  passkeyNameProblem,
  passkeySupported,
  registerPasskey,
  secondFactorPending,
} from "@/lib/auth/passkey";
import { requestStepUp } from "@/lib/api/step-up";
import { sessionLanding, singleWorkspaceSlug } from "@/lib/auth/auth-gate";
import { postAuthRedirect } from "@/lib/auth/safe-next";
import { startTotpEnrolment, TOTP_SETUP_DECLINED_MESSAGE } from "@/lib/auth/totp-enrolment";
import { securityChangeError } from "@/lib/two-factor-security";
import { TotpQr } from "@/components/auth/totp-qr";
import { StepUpPrompt } from "@/components/auth/step-up-prompt";
import { Label } from "@/components/ui/label";
import type { TOTPSetupResponse } from "@/types/auth";

const twoFaSetupSearchSchema = z.object({
  next: z.string().optional().catch(undefined),
  // Coerced to a number so the URL form ?required=1 stays a single
  // numeric literal. Falsy → optional/skippable flow.
  required: z.coerce.number().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/2fa-setup")({
  validateSearch: twoFaSetupSearchSchema,
  component: TwoFactorSetupPage,
});

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success(`${label} copied`);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Couldn't copy — select and copy manually");
        }
      }}
    >
      {copied ? (
        <Check className="h-4 w-4" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
      Copy
    </Button>
  );
}

function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const { next: nextRaw, required } = Route.useSearch();
  const { refresh, user, activate } = useAuth();
  // "required" arrivals come from TWO distinct triggers: the login-time
  // role mandate (super_admin / hr_manager must enrol before opening any
  // workspace, M-1) and a step-up ACTION (payroll lock, ICC, F&F) by a
  // role with no such mandate. The copy used to claim "your role
  // requires…" for both — wrong for a company_admin who just clicked
  // "Lock payroll" (2026-07-11 UX audit 🟡 #10). Distinguish by role.
  // Persona round 2026-10: the M-1 enrolment session is identity-only (no
  // workspace role yet), so a first sign-in fell through to the "action
  // you tried" copy. The destination tells sign-in from a step-up action.
  const reason = twoFactorSetupReason({
    required: required === 1,
    role: user?.role,
    next: safeNext(nextRaw),
    enrollmentRequired: user?.enrollment_required === true,
  });
  // "choose" → the two options; "passkey" / "totp" → that option's form.
  // null until we know whether this browser can use passkeys.
  const [mode, setMode] = useState<"choose" | "passkey" | "totp" | null>(null);
  const [passkeyName, setPasskeyName] = useState(() => defaultPasskeyName());
  const [canPasskey, setCanPasskey] = useState(false);
  // Same value for callbacks that must not re-create on change.
  const canPasskeyRef = useRef(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [enrol, setEnrol] = useState<TOTPSetupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Guard the post-setup redirect against open-redirect.
  const nextPath = safeNext(nextRaw);
  const isMandatory = required === 1;

  const form = useForm<TotpCodeValues>({
    resolver: zodResolver(totpCodeSchema),
    defaultValues: { code: "" },
  });

  // Guards against React 19 StrictMode invoking the effect twice: a
  // second /totp/setup would stage a fresh secret server-side and
  // silently invalidate the secret the user just scanned.
  const enrolStarted = useRef(false);

  // Round 2 T-H1: a temporary password is changed BEFORE any 2FA method is
  // added (the server refuses setup with 403 password_change_required).
  const toChangePassword = useCallback(() => {
    void navigate({ to: "/change-password", search: { next: nextPath }, replace: true });
  }, [navigate, nextPath]);

  const startEnrolment = useCallback(async () => {
    // lib/auth/totp-enrolment: a method holder without a fresh step-up is
    // asked to confirm (the prompt is mounted below) and the setup retried
    // once — never an error the "Try again" button loops on (round 2 F-I1).
    const out = await startTotpEnrolment(() => authApi.totpSetup(), toApiError);
    setLoading(false);
    switch (out.kind) {
      case "staged":
        setEnrol(out.setup);
        return;
      case "already_enabled":
        toast.info("Two-factor authentication is already on.");
        void navigate({ to: nextPath, replace: true });
        return;
      case "password_change_required":
        toChangePassword();
        return;
      case "declined":
        enrolStarted.current = false;
        toast.info(TOTP_SETUP_DECLINED_MESSAGE);
        setMode(canPasskeyRef.current ? "choose" : "totp");
        if (!canPasskeyRef.current) setSetupError(TOTP_SETUP_DECLINED_MESSAGE);
        return;
      default:
        setSetupError(out.message);
    }
  }, [navigate, nextPath, toChangePassword]);

  // Shared by both methods: continue exactly as after /2fa-verify.
  async function finishEnrolment(message: string) {
    const me = await refresh();
    toast.success(message);
    // M-1 path: enrolment was forced on an IDENTITY-ONLY session
    // (user_id===0). With exactly one workspace, open it directly (round 2
    // E-2: no one-row "Choose a workspace"); with several, the picker.
    if (me && me.user_id === 0 && !me.must_change_password && !me.enrollment_required) {
      try {
        const slug = singleWorkspaceSlug(await authApi.getMyWorkspaces());
        if (slug) {
          const res = await activate(slug);
          if (secondFactorPending(res)) {
            void navigate({ to: "/2fa-verify", search: { next: nextPath }, replace: true });
            return;
          }
          void navigate({ to: postAuthRedirect(nextPath, slug, res), replace: true });
          return;
        }
      } catch {
        // Fall through to the picker, which shows its own errors.
      }
    }
    const route = sessionLanding(me, nextPath);
    void navigate({ to: route.to, search: route.search as never, replace: true });
  }

  async function addPasskey(e: React.FormEvent) {
    e.preventDefault();
    const name = passkeyName.trim();
    const problem = passkeyNameProblem(name);
    if (problem) {
      setPasskeyError(problem);
      return;
    }
    setPasskeyBusy(true);
    setPasskeyError(null);
    try {
      let res;
      try {
        res = await registerPasskey(name);
      } catch (err) {
        // Already has a method → confirm with it first, then retry once.
        if (toApiError(err).error !== "step_up_required") throw err;
        if (!(await requestStepUp())) return;
        res = await registerPasskey(name);
      }
      if (isPasskeyCancelled(res)) return;
      await finishEnrolment("Passkey added.");
    } catch (err) {
      const apiErr = toApiError(err);
      if (apiErr.error === "password_change_required") {
        toChangePassword();
        return;
      }
      setPasskeyError(
        isAlreadyRegisteredError(err)
          ? "This device already has a passkey for your account."
          : securityChangeError(apiErr.error, apiErr.message, "The passkey could not be added. Try again."),
      );
    } finally {
      setPasskeyBusy(false);
    }
  }

  const retryEnrolment = useCallback(() => {
    setEnrol(null);
    setSetupError(null);
    setLoading(true);
    void startEnrolment();
  }, [startEnrolment]);

  const chooseAuthenticator = useCallback(() => {
    setMode("totp");
    if (enrolStarted.current) return;
    enrolStarted.current = true;
    setLoading(true);
    void startEnrolment();
  }, [startEnrolment]);

  // Read the session fresh on arrival (a sign-in refusal or a picker
  // activate may have sent us here before /me was hydrated): a temporary
  // password goes to /change-password first (round 2 T-H1), and nothing —
  // not even the authenticator secret — is staged before that is known.
  const [sessionChecked, setSessionChecked] = useState(false);
  useEffect(() => {
    let active = true;
    void refresh().then((me) => {
      if (!active) return;
      if (me?.must_change_password) {
        toChangePassword();
        return;
      }
      setSessionChecked(true);
    });
    return () => {
      active = false;
    };
  }, [refresh, toChangePassword]);

  // Passkey first when the browser can; otherwise straight to the
  // authenticator app (the old single-method page).
  useEffect(() => {
    if (!sessionChecked) return;
    let active = true;
    void passkeySupported().then((ok) => {
      if (!active) return;
      canPasskeyRef.current = ok;
      setCanPasskey(ok);
      if (ok) setMode((m) => m ?? "choose");
      else chooseAuthenticator();
    });
    return () => {
      active = false;
    };
  }, [sessionChecked, chooseAuthenticator]);

  async function onSubmit(values: TotpCodeValues) {
    setSubmitting(true);
    try {
      await authApi.totpVerify({ code: values.code });
      await finishEnrolment("Two-factor authentication enabled.");
    } catch (err) {
      handleAuthError(err, form.setError, ["code"]);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
    {/* Adding a passkey on top of an existing method asks for a fresh
        step-up; outside the workspace layout this page mounts the prompt. */}
    <StepUpPrompt />
    <Card>
      <CardHeader>
        <div className="mb-1 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <CardTitle className="text-2xl">
            {isMandatory
              ? "Two-factor authentication is required"
              : "Set Up Two-Factor Authentication"}
          </CardTitle>
        </div>
        <CardDescription>
          {twoFactorSetupDescription(reason)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {mode === null && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {mode === "choose" && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setMode("passkey")}
              className="flex w-full items-start gap-3 rounded-lg border-2 border-primary/40 bg-primary/5 p-4 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="choose-passkey"
            >
              <KeyRound aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span className="space-y-1">
                <span className="block text-sm font-medium">Add a passkey (recommended)</span>
                <span className="block text-xs text-muted-foreground">
                  Sign in with your fingerprint, face or device PIN — nothing
                  to type, and it can&apos;t be phished.
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={chooseAuthenticator}
              className="flex w-full items-start gap-3 rounded-lg border border-input p-4 text-left transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="choose-authenticator"
            >
              <Smartphone aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="space-y-1">
                <span className="block text-sm font-medium">Use an authenticator app</span>
                <span className="block text-xs text-muted-foreground">
                  Google Authenticator, Authy, 1Password… — enter a 6-digit
                  code when asked.
                </span>
              </span>
            </button>
          </div>
        )}

        {mode === "passkey" && (
          <form onSubmit={addPasskey} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="passkey-name">Passkey Name</Label>
              <Input
                id="passkey-name"
                value={passkeyName}
                maxLength={60}
                onChange={(e) => setPasskeyName(e.target.value)}
                data-testid="passkey-name"
              />
              <p className="text-xs text-muted-foreground">
                Helps you tell your passkeys apart later, e.g. &ldquo;Work laptop&rdquo;.
              </p>
            </div>
            {passkeyError && (
              <p role="alert" className="text-sm text-destructive">
                {passkeyError}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={passkeyBusy} data-testid="add-passkey">
              {passkeyBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              Add Passkey
            </Button>
            <p className="text-center text-sm">
              <button
                type="button"
                onClick={() => setMode("choose")}
                className="font-medium text-primary hover:underline"
                disabled={passkeyBusy}
              >
                Choose Another Way
              </button>
            </p>
          </form>
        )}

        {mode === "totp" && loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {mode === "totp" && !loading && setupError && (
          <div className="space-y-3">
            <p className="text-sm text-destructive">{setupError}</p>
            <Button
              type="button"
              variant="outline"
              onClick={retryEnrolment}
            >
              Try Again
            </Button>
          </div>
        )}

        {mode === "totp" && !loading && enrol && (
          <>
            <div className="space-y-3">
              <p className="text-sm font-medium">
                1. Scan this QR code with your authenticator
              </p>
              <p className="text-xs text-muted-foreground">
                The code is rendered on this device — your secret is
                never sent to any third-party service.
              </p>
              <TotpQr otpauthUrl={enrol.otpauth_url} />

              <details className="rounded-md border border-input bg-secondary/60 p-3">
                <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                  Can&apos;t scan? Enter the secret manually
                </summary>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 rounded-md border border-input bg-secondary p-3">
                    <code className="flex-1 break-all text-sm">
                      {enrol.secret}
                    </code>
                    <CopyButton value={enrol.secret} label="Secret" />
                  </div>
                  <div className="flex items-center gap-2 rounded-md border border-input bg-secondary p-3">
                    <code className="flex-1 break-all text-xs text-muted-foreground">
                      {enrol.otpauth_url}
                    </code>
                    <CopyButton
                      value={enrol.otpauth_url}
                      label="Setup URI"
                    />
                  </div>
                </div>
              </details>
            </div>

            <div className="space-y-2 border-t border-border pt-4">
              <p className="text-sm font-medium">
                2. Confirm with a 6-digit code
              </p>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                  noValidate
                >
                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="sr-only">
                          Authenticator Code
                        </FormLabel>
                        <FormControl>
                          <Input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            maxLength={6}
                            className="text-center text-lg tracking-[0.5em]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={submitting}
                  >
                    {submitting && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    Enable Two-Factor Authentication
                  </Button>
                </form>
              </Form>
            </div>
          </>
        )}

        {mode === "totp" && canPasskey && (
          <p className="text-center text-sm">
            <button
              type="button"
              onClick={() => setMode("choose")}
              className="font-medium text-primary hover:underline"
              disabled={submitting}
            >
              Choose Another Way
            </button>
          </p>
        )}
      </CardContent>
    </Card>
    </>
  );
}
